<template>
  <div class="af">
    <!-- ===== HERO HEADER ===== -->
    <div class="af-hero">
      <div class="af-hero__left">
        <div class="af-hero__avatar">
          <i class="fa-light fa-location-dot" />
        </div>
        <div class="af-hero__identity">
          <h2 class="af-hero__name">
            {{ title }}
          </h2>
          <div class="af-hero__meta">
            <DataExplorerLink
              :id="selectedData.idShopCustomer"
              :value="selectedData.tabLabel"
              :show-icon="true"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- ===== MAIN 2-COL LAYOUT ===== -->
    <div class="af-layout">
      <!-- ===== LEFT COLUMN: Address info ===== -->
      <div class="af-left">
        <!-- Address details -->
        <div class="af-card">
          <div class="af-card__header">
            <i class="fa-light fa-address-book" />
            <h3>{{ t('dataExplorer.addressFile.fields.addressDetails') }}</h3>
          </div>
          <div class="af-card__body">
            <div class="af-info-row">
              <div class="af-info-row__icon">
                <i class="fa-light fa-road" />
              </div>
              <div class="af-info-row__content">
                <span class="af-info-row__label">{{ t('dataExplorer.addressFile.fields.address1') }}</span>
                <span class="af-info-row__value">{{ selectedData.address1 || '-' }}</span>
              </div>
            </div>
            <div class="af-info-row">
              <div class="af-info-row__icon">
                <i class="fa-light fa-signs-post" />
              </div>
              <div class="af-info-row__content">
                <span class="af-info-row__label">{{ t('dataExplorer.addressFile.fields.address2') }}</span>
                <span class="af-info-row__value">
                  {{ selectedData.address2 || '' }}
                  <span
                    v-if="!selectedData.address2"
                    class="af-info-row__empty"
                  >-</span>
                </span>
              </div>
            </div>
            <div class="af-info-row">
              <div class="af-info-row__icon">
                <i class="fa-light fa-hashtag" />
              </div>
              <div class="af-info-row__content">
                <span class="af-info-row__label">{{ t('dataExplorer.addressFile.fields.postcode') }}</span>
                <span class="af-info-row__value">{{ selectedData.postcode || '-' }}</span>
              </div>
            </div>
            <div class="af-info-row">
              <div class="af-info-row__icon">
                <i class="fa-light fa-city" />
              </div>
              <div class="af-info-row__content">
                <span class="af-info-row__label">{{ t('dataExplorer.addressFile.fields.city') }}</span>
                <span class="af-info-row__value">{{ selectedData.city || '-' }}</span>
              </div>
            </div>
            <div class="af-info-row">
              <div class="af-info-row__icon">
                <i class="fa-light fa-globe" />
              </div>
              <div class="af-info-row__content">
                <span class="af-info-row__label">{{ t('dataExplorer.addressFile.fields.country') }}</span>
                <span class="af-info-row__value">{{ country.label }}</span>
              </div>
            </div>
            <div class="af-info-row">
              <div class="af-info-row__icon">
                <i class="fa-light fa-phone" />
              </div>
              <div class="af-info-row__content">
                <span class="af-info-row__label">{{ t('dataExplorer.addressFile.fields.tel') }}</span>
                <span class="af-info-row__value">{{ formatPhoneNumber(selectedData.phone1, country.code) }}</span>
              </div>
            </div>
            <div class="af-info-row">
              <div class="af-info-row__icon">
                <i class="fa-light fa-mobile-alt" />
              </div>
              <div class="af-info-row__content">
                <span class="af-info-row__label">{{ t('dataExplorer.addressFile.fields.mobile') }}</span>
                <span class="af-info-row__value">{{ formatPhoneNumber(selectedData.phone2, country.code) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== RIGHT COLUMN: Map & orders ===== -->
      <div class="af-right">
        <!-- Map -->
        <div class="af-card">
          <div class="af-card__header">
            <i class="fa-light fa-map" />
            <h3>{{ t('dataExplorer.addressFile.fields.map') }}</h3>
          </div>
          <div class="af-card__body af-card__body--no-pad">
            <div class="af-map">
              <AddressMap
                :address="selectedData"
                class-name="af-map__inner"
              />
            </div>
          </div>
        </div>

        <!-- Invoice orders -->
        <div class="af-card">
          <div class="af-card__header af-card__header--no-border">
            <i class="fa-light fa-file-invoice" />
            <h3>{{ t('dataExplorer.addressFile.fields.orders.invoiceOrders') }}</h3>
          </div>
          <div class="af-card__body af-card__body--padded">
            <List
              :list="selectedData.invoiceOrders.data"
              :retrieve-data="getAddressOrders"
              :retrieve-data-arguments="[selectedData.idAddressShop, 'so.id_address_invoice']"
              :data-loaded="selectedData.invoiceOrders.dataLoaded"
              :no-column="true"
              :total="selectedData.invoiceOrders.total"
              :current-page="selectedData.invoiceOrders.currentPage"
              key-name="idShopOrder"
              @loaded="(orders, total) => handleRetrieveOrders(orders, 'invoiceOrders', total)"
              @update-page="(currentPage) => handleOrdersNextPage(currentPage, 'invoiceOrders')"
            >
              <template #content="{ item }">
                <OrderItem
                  :order="item"
                  key-name="invoiceOrders"
                />
              </template>
            </List>
          </div>
        </div>

        <!-- Delivery orders -->
        <div class="af-card">
          <div class="af-card__header af-card__header--no-border">
            <i class="fa-light fa-truck" />
            <h3>{{ t('dataExplorer.addressFile.fields.orders.deliveryOrders') }}</h3>
          </div>
          <div class="af-card__body af-card__body--padded">
            <List
              :list="selectedData.deliveryOrders.data"
              :retrieve-data="getAddressOrders"
              :retrieve-data-arguments="[selectedData.idAddressShop, 'so.id_address_delivery']"
              :data-loaded="selectedData.deliveryOrders.dataLoaded"
              :no-column="true"
              :total="selectedData.deliveryOrders.total"
              :current-page="selectedData.deliveryOrders.currentPage"
              key-name="idShopOrder"
              @loaded="(orders, total) => handleRetrieveOrders(orders, 'deliveryOrders', total)"
              @update-page="(currentPage) => handleOrdersNextPage(currentPage, 'deliveryOrders')"
            >
              <template #content="{ item }">
                <OrderItem
                  :order="item"
                  key-name="deliveryOrders"
                />
              </template>
            </List>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script lang="ts">
import { computed, defineComponent } from 'vue';

import List from '@/components/data-explorer/fields/List.vue';
import DataExplorerLink from '@/components/data-explorer/fields/DataExplorerLink.vue';
import OrderItem from '@/components/data-explorer/fields/OrderItem.vue';
import AddressMap from '@/components/data-explorer/fields/AddressMap.vue';

import {
  getAddressOrders,
} from '@/composables/data-explorer/DataExplorer';

import { countries } from '@/types/country-language-options';
import {
  Order,
} from '@/types/data-explorer-types';

import { useStore } from '@/store';

import { useI18n } from 'vue-i18n';
import { CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';

export default defineComponent({
  name: 'AddressFile',

  components: {
    DataExplorerLink,
    List,
    OrderItem,
    AddressMap,
  },

  setup() {
    const { t, locale } = useI18n();
    const store = useStore();

    const selectedData = computed(() => store.getters['dataExplorer/getSelectedData']);
    const country = computed(() => {
      if (selectedData.value) {
        const item = countries.find((countryItem) => countryItem.code.toLowerCase() === (selectedData.value.country || '').toLowerCase());
        if (item) {
          return {
            code: item.code,
            label: item.label,
          };
        }
      }
      return {
        label: '-',
        code: '',
      };
    });

    const title = computed(() => {
      if (selectedData.value) {
        return t(`dataExplorer.tabs.${selectedData.value.dataType}`, [selectedData.value.tabLabel])
      }
      return '';
    });

    const formatPhoneNumber = (phoneNumber: string, country: string) => {
      if (phoneNumber && country) {
        const phoneNumberObj = parsePhoneNumberFromString(phoneNumber, country.toUpperCase() as CountryCode);
        if (phoneNumberObj && phoneNumberObj.isValid()) {
          return phoneNumberObj.formatInternational();
        }
        if (phoneNumber) {
          return phoneNumber;
        }
      }
      return '-';
    };

    const handleRetrieveOrders = (orders: Array<Order>, dataKey: string, total: number) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          newLists: orders,
          dataKey,
          total,
        }
      );
    };

    const handleOrdersNextPage = (currentPage: number, dataKey: string) => {
      store.dispatch(
        'dataExplorer/setAdditionnalData',
        {
          dataId: selectedData.value.dataId,
          currentPage,
          dataKey,
        }
      );
    };

    return {
      t,
      locale,
      selectedData,
      title,
      country,

      getAddressOrders,
      formatPhoneNumber,
      handleRetrieveOrders,
      handleOrdersNextPage,
    }
  }
})
</script>

<style lang="scss" scoped>
// ===== MAIN CONTAINER =====
.af {
  padding: 1.25rem;
}

// ===== HERO HEADER =====
.af-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #e5e7eb;

  &__left {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #06b6d4, #0891b2);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: #fff;
      font-size: 1.25rem;
    }
  }

  &__identity {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__name {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0;
    line-height: 1.2;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8125rem;
  }
}

// ===== 2-COL LAYOUT =====
.af-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 1rem;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.af-left {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.af-right {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

// ===== CARD =====
.af-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.4375rem;
    padding: 0.625rem 1rem;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;

    &--no-border {
      border-bottom: none;
    }

    i {
      color: #06b6d4;
      font-size: 0.875rem;
    }

    h3 {
      font-size: 0.75rem;
      font-weight: 700;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin: 0;
    }
  }

  &__body {
    padding: 0.375rem 0;

    &--no-pad {
      padding: 0;
    }

    &--padded {
      padding: 0.5rem 0.75rem;
    }
  }
}

// ===== INFO ROWS =====
.af-info-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.375rem 0.75rem;
  transition: background 0.1s;

  &:hover {
    background: #f9fafb;
  }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 7px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 0.8125rem;
      color: #64748b;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.0625rem;
  }

  &__label {
    font-size: 0.6875rem;
    font-weight: 500;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__value {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #1e293b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__empty {
    color: #d1d5db;
  }
}

// ===== MAP =====
.af-map {
  height: 200px;
  width: 100%;

  :deep(.af-map__inner) {
    height: 200px !important;
    width: 100% !important;
    border-radius: 0 0 10px 10px;
  }
}
</style>
